import { useRef, useState } from 'react';
import { Download, Paperclip, Trash2, Upload } from 'lucide-react';
import type { TaskAttachment, UserSummary } from '@ssn/shared';
import { Button, IconButton } from '@/components/ui/Button';
import { InlineError } from '@/components/ui/Feedback';
import { formatBytes, formatRelativeTime } from '@/lib/format';
import { useDeleteAttachment, useUploadAttachment } from '@/lib/queries';

export function Attachments({
  reference,
  attachments,
  currentUser,
  canUpload,
  canDeleteAny,
}: {
  reference: number;
  attachments: readonly TaskAttachment[];
  currentUser: UserSummary;
  canUpload: boolean;
  canDeleteAny: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const upload = useUploadAttachment(reference);
  const remove = useDeleteAttachment(reference);

  const send = (file: File | undefined) => {
    if (file) upload.mutate(file);
  };

  return (
    <div className="space-y-3">
      {attachments.length > 0 && (
        <ul className="space-y-1.5">
          {attachments.map((attachment) => {
            const canRemove = canDeleteAny || attachment.uploadedBy.id === currentUser.id;
            return (
              <li
                key={attachment.id}
                className="flex items-center gap-2.5 rounded-md border border-border bg-surface-sunken px-2.5 py-2"
              >
                <Paperclip aria-hidden className="size-4 shrink-0 text-text-subtle" />
                <div className="min-w-0 flex-1">
                  <a
                    href={attachment.downloadUrl}
                    className="block truncate text-sm text-text hover:text-accent-text hover:underline"
                  >
                    {attachment.filename}
                  </a>
                  <p className="text-[11px] text-text-subtle">
                    {formatBytes(attachment.byteSize)} · {attachment.uploadedBy.displayName} ·{' '}
                    {formatRelativeTime(attachment.uploadedAt)}
                  </p>
                </div>
                <a
                  href={attachment.downloadUrl}
                  aria-label={`Download ${attachment.filename}`}
                  title={`Download ${attachment.filename}`}
                  className="inline-flex size-8 items-center justify-center rounded-md text-text-muted hover:bg-surface-hover hover:text-text"
                >
                  <Download aria-hidden className="size-4" />
                </a>
                {canRemove && (
                  <IconButton
                    label={`Remove ${attachment.filename}`}
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(`Remove ${attachment.filename}?`)) remove.mutate(attachment.id);
                    }}
                  >
                    <Trash2 aria-hidden className="size-4" />
                  </IconButton>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canUpload && (
        <div
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            send(event.dataTransfer.files[0]);
          }}
          className={`rounded-md border border-dashed px-3 py-4 text-center transition-colors ${
            dragging ? 'border-accent bg-accent-soft' : 'border-border bg-surface'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            className="sr-only"
            onChange={(event) => {
              send(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          <p className="text-xs text-text-muted">Drop a file here, or</p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="mt-2"
            loading={upload.isPending}
            onClick={() => inputRef.current?.click()}
            icon={<Upload aria-hidden className="size-3.5" />}
          >
            Choose a file
          </Button>
        </div>
      )}

      {attachments.length === 0 && !canUpload && (
        <p className="text-xs text-text-subtle">No attachments.</p>
      )}

      <InlineError error={upload.error} />
      <InlineError error={remove.error} />
    </div>
  );
}
